import { Account } from '@eagle/core-data-types';
import { Menu, MenuItem, Skeleton, Stack, styled, SvgIcon, Tooltip, Typography } from '@ethings/ui-toolkit';
import { Box, Link, Avatar as MUIAvatar, Theme } from '@mui/material';
import { ResponsiveStyleValue, SxProps } from '@mui/system';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { FC, ReactNode, useState } from 'react';
import { useAuthenticated } from '../../auth';
import { Nullable } from '../../types';
import { FILTER_OUT, testid } from '../../util';
import { Icon, WidgetIconProps } from '../icons/widget-icon';
import { AccountSwitcher } from './account-switcher';

interface Props {
  background?: string;
  'data-testid'?: string;
  chevronIcon?: ReactNode;
  displayAccountSwitcher?: boolean;
  handleNavigate?: () => void;
  icon?: WidgetIconProps['icon'];
  iconColor?: string;
  image?: string;
  muiAvatarSx?: SxProps;
  onSelected?: (value: string) => void;
  options?: Account[];
  selected?: string;
  showIconOnly?: boolean;
  spacing?: ResponsiveStyleValue<string | number>;
  subtitle?: string;
  sx?: SxProps;
  tabIndex?: number;
  title?: string;
  tooltip?: string;
}

interface StyledTypographyProps {
  handleNavigate?: () => void;
}

const StyledSvgIcon = styled(SvgIcon)(({ theme }: { theme?: Theme }) => ({
  height: '12px',
  marginLeft: theme?.spacing(0.5),
  width: '12px',
}));

const StyledTypography = styled(Typography)<StyledTypographyProps>(({ handleNavigate }) => ({
  cursor: handleNavigate ? 'pointer' : 'auto',
}));

const StyledMenu = styled(Menu)({
  zIndex: 1400,
});

export const Avatar: FC<Props> = ({
  background,
  chevronIcon,
  displayAccountSwitcher = false,
  handleNavigate,
  icon,
  iconColor,
  image,
  muiAvatarSx,
  onSelected,
  options,
  showIconOnly,
  spacing = 2,
  subtitle,
  tabIndex,
  title,
  tooltip,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);
  const hasSwitchableAccountQuery = useHasSwitchableAccountQuery(displayAccountSwitcher);

  const open = Boolean(anchorEl);

  const clickHandler = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleSelected = (event: React.MouseEvent<HTMLElement>): void => {
    if (onSelected) {
      const { value } = event.currentTarget.dataset;
      if (value) {
        onSelected(value);
      }
    }
    handleClose();
  };

  const keyupHandler = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (handleNavigate && event.code === 'Enter') {
      handleNavigate?.();
    }
  };

  const renderSubtitle = (): ReactNode => {
    if (displayAccountSwitcher && hasSwitchableAccountQuery.status === 'pending') {
      return (
        <Typography component="div" lineHeight="inherit" variant='caption'>
          <Skeleton width="60%" />
        </Typography>
      );
    }

    if ((displayAccountSwitcher && hasSwitchableAccountQuery.data === true) || options?.length) {
      return (
        <>
          <Link onClick={clickHandler} sx={{ lineHeight: 'inherit', textAlign: 'left' }} data-testid="dropdown-button" component='button' variant='caption' color='inherit' underline='none'>
            <Stack direction="row" alignItems="center">
              <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" >{subtitle}</Box>
              {chevronIcon ? chevronIcon : <StyledSvgIcon><path d="m1 8 10 10 10-10z"></path></StyledSvgIcon>}
            </Stack>
          </Link>
          {displayAccountSwitcher ? (
            <AccountSwitcher anchorEl={anchorEl} onClose={handleClose} onSelect={onSelected} />
          ) :
            <StyledMenu
              data-testid="dropdown-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              {options?.filter((item) => !item.deleted).map((prop, i) => ((<MenuItem key={i} data-testid={`dropdown-menu-item-${i}`} data-value={prop._id} onClick={handleSelected}>{prop.display}</MenuItem>)))}
            </StyledMenu>
          }
        </>
      );
    }

    return (
      <Typography lineHeight="inherit" margin={0} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" variant="caption">
        {subtitle}
      </Typography>
    );
  };

  return <Stack alignItems="center" direction="row" spacing={spacing} {...props}>
    <Box>
      <Tooltip title={tooltip}>
        <MUIAvatar
          tabIndex={handleNavigate && tabIndex}
          onClick={() => handleNavigate?.()}
          onKeyUp={keyupHandler}
          title={tooltip}
          src={image}
          sx={{
            backgroundColor: background ? background : null,
            color: iconColor ? iconColor : null,
            cursor: handleNavigate ? 'pointer' : 'auto',
            ...muiAvatarSx,
          }}
        >{icon && <Icon icon={icon} data-testid={testid`icon-${icon}`} />}</MUIAvatar>
      </Tooltip>

    </Box>
    {title && (
      <Box className='avatar' sx={{ lineHeight: 1, display: 'flex', flexDirection: 'column', gap: '2px', transition: '250ms opacity ease', opacity: !showIconOnly ? 1 : 0 }}>
        <StyledTypography
          data-testid='avatar-title'
          onClick={() => handleNavigate?.()}
          onKeyUp={keyupHandler}
          tabIndex={(handleNavigate && tabIndex) && (tabIndex++)}
          title={tooltip}
          lineHeight="inherit"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          variant="body1"
        >
          {title}
        </StyledTypography>
        {renderSubtitle()}
      </Box>
    )}
  </Stack >;
};

const useHasSwitchableAccountQuery = (enabled?: boolean): UseQueryResult<boolean> => {
  const { restClient, userInfo } = useAuthenticated();

  return useQuery({
    queryKey: ['has-switchable-account', userInfo.baseAccountId],
    queryFn: async () => {
      const accounts = await restClient.my.accounts.getAll({
        limit: 1,
        filter: {
          ...FILTER_OUT.deleted,
          _id: { $ne: userInfo.baseAccountId },
        },
      });
      return accounts.length > 0;
    },
    enabled,
  });
};
